import { useCallback, useContext } from 'react';

import { RouteContext } from 'src/context';
import type { ActivationOnlyTaskCode } from 'src/entities/OnboardingStatus';
import history from 'src/history';
import { useAppDispatch } from 'src/hooks/useStore';
import { useUpdateStudioTaskStatusMutation } from 'src/services/api/improvedOnboardingApi';
import { alertSnackbar } from 'src/store/ui/actions';
import { ensureApiError } from 'src/utils/Errors';

import { GUIDED_TOUR_PARAM } from './studioChecklist';

/**
 * Whether the page was opened for this guided tour, and a way to drop the param
 * so the guide doesn't reopen on refresh or back navigation.
 */
export const useGuidedTourParam = (tour: string) => {
  const { query, pathname } = useContext(RouteContext);
  const isActive = query[GUIDED_TOUR_PARAM] === tour;

  const clear = useCallback(() => {
    const params = new URLSearchParams(query);
    params.delete(GUIDED_TOUR_PARAM);
    const search = params.toString();
    history.replace(`${pathname}${search ? `?${search}` : ''}`);
  }, [query, pathname]);

  return { isActive, clear };
};

/** "Do this later" on a checklist 2 guide: checks the step off as skipped. */
export const useSkipActivationStep = () => {
  const dispatch = useAppDispatch();
  const [updateStudioTaskStatus] = useUpdateStudioTaskStatusMutation();

  return useCallback(
    async (taskCode: ActivationOnlyTaskCode) => {
      try {
        await updateStudioTaskStatus({ taskCode, status: 'skipped' }).unwrap();
      } catch (err) {
        dispatch(alertSnackbar({ errorMessage: ensureApiError(err).message }));
      }
    },
    [dispatch, updateStudioTaskStatus],
  );
};
